/**
 * React Query hooks for playlists + favorites (web). Mirrors the mobile
 * media-library behaviour: infinite browse, detail, create/update/delete/
 * duplicate, and an optimistic favorites toggle.
 */
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import * as api from '../api/mediaApi';
import type {
  AudioPlaylistDetailDto,
  AudioPlaylistTrackDto,
  CreatePlaylistRequest,
  PlaylistFilterParams,
  UpdatePlaylistRequest,
} from '../types';

const PAGE_SIZE = 24;

export const playlistKeys = {
  all: ['playlists'] as const,
  lists: () => [...playlistKeys.all, 'list'] as const,
  list: (params: PlaylistFilterParams) => [...playlistKeys.lists(), params] as const,
  details: () => [...playlistKeys.all, 'detail'] as const,
  detail: (id: string) => [...playlistKeys.details(), id] as const,
  favorites: () => [...playlistKeys.all, 'favorites'] as const,
  categories: () => ['library', 'categories'] as const,
};

/** Infinite, paginated playlist browse. */
export function usePlaylistsInfinite(params: PlaylistFilterParams) {
  return useInfiniteQuery({
    queryKey: playlistKeys.list(params),
    queryFn: ({ pageParam }) => api.listPlaylists({ ...params, page: pageParam, pageSize: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
  });
}

export function usePlaylist(id: string | undefined) {
  return useQuery({
    queryKey: playlistKeys.detail(id ?? ''),
    queryFn: () => api.getPlaylistById(id!),
    enabled: !!id,
  });
}

export function useLibraryFilterOptions() {
  return useQuery({
    queryKey: playlistKeys.categories(),
    queryFn: api.getLibraryFilterOptions,
    staleTime: 5 * 60_000,
  });
}

export function useCreatePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePlaylistRequest) => api.createPlaylist(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: playlistKeys.lists() }),
  });
}

export function useUpdatePlaylist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdatePlaylistRequest) => api.updatePlaylist(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: playlistKeys.detail(id) });
      qc.invalidateQueries({ queryKey: playlistKeys.lists() });
    },
  });
}

export function useDeletePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deletePlaylist(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: playlistKeys.lists() }),
  });
}

export function useDuplicatePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.duplicatePlaylist(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: playlistKeys.lists() }),
  });
}

/** Favorite playlist ids as a Set, plus an optimistic toggle. */
export function useFavorites() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: playlistKeys.favorites(),
    queryFn: api.getFavorites,
    staleTime: 60_000,
  });

  const favoriteIds = new Set(query.data?.playlistIds ?? []);

  const toggle = useMutation({
    mutationFn: ({ id, isFavorite }: { id: string; isFavorite: boolean }) =>
      isFavorite ? api.removeFavorite(id) : api.addFavorite(id),
    onMutate: async ({ id, isFavorite }) => {
      await qc.cancelQueries({ queryKey: playlistKeys.favorites() });
      const previous = qc.getQueryData<{ playlistIds: string[] }>(playlistKeys.favorites());
      const next = new Set(previous?.playlistIds ?? []);
      if (isFavorite) next.delete(id);
      else next.add(id);
      qc.setQueryData(playlistKeys.favorites(), { playlistIds: [...next] });
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(playlistKeys.favorites(), ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: playlistKeys.favorites() }),
  });

  return {
    favoriteIds,
    isLoading: query.isLoading,
    toggleFavorite: (id: string) => toggle.mutate({ id, isFavorite: favoriteIds.has(id) }),
  };
}

// --- Editing (own playlists) ---

/** Drag-reorder tracks; optimistically reorders the detail cache. */
export function useReorderTracks(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedTrackIds: string[]) => api.reorderPlaylistTracks(playlistId, orderedTrackIds),
    onMutate: async (orderedTrackIds) => {
      await qc.cancelQueries({ queryKey: playlistKeys.detail(playlistId) });
      const previous = qc.getQueryData<AudioPlaylistDetailDto>(playlistKeys.detail(playlistId));
      if (previous) {
        const byId = new Map(previous.tracks.map((t) => [t.id, t]));
        const reordered = orderedTrackIds
          .map((id) => byId.get(id))
          .filter((t): t is AudioPlaylistTrackDto => !!t)
          .map((t, i) => ({ ...t, position: i + 1 }));
        qc.setQueryData<AudioPlaylistDetailDto>(playlistKeys.detail(playlistId), {
          ...previous,
          tracks: reordered,
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(playlistKeys.detail(playlistId), ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) }),
  });
}

/** Add one or more content items as tracks; skips per-track failures (e.g. duplicates). */
export function useAddTracks(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contentIds: string[]) => {
      for (const contentId of contentIds) {
        try {
          await api.addTrackToPlaylist(playlistId, contentId);
        } catch {
          // ignore individual failures (already present / 400) — matches mobile
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      qc.invalidateQueries({ queryKey: playlistKeys.lists() });
    },
  });
}

export function useRemoveTrack(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trackId: string) => api.removeTrackFromPlaylist(playlistId, trackId),
    onMutate: async (trackId) => {
      await qc.cancelQueries({ queryKey: playlistKeys.detail(playlistId) });
      const previous = qc.getQueryData<AudioPlaylistDetailDto>(playlistKeys.detail(playlistId));
      if (previous) {
        qc.setQueryData<AudioPlaylistDetailDto>(playlistKeys.detail(playlistId), {
          ...previous,
          tracks: previous.tracks.filter((t) => t.id !== trackId),
          trackCount: Math.max(0, previous.trackCount - 1),
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(playlistKeys.detail(playlistId), ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      qc.invalidateQueries({ queryKey: playlistKeys.lists() });
    },
  });
}

export function useUploadCover(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadPlaylistCover(playlistId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      qc.invalidateQueries({ queryKey: playlistKeys.lists() });
    },
  });
}

export function useDeleteCover(playlistId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.deletePlaylistCover(playlistId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      qc.invalidateQueries({ queryKey: playlistKeys.lists() });
    },
  });
}
